"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Carousel = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const index_js_1 = require("../utils/index.js");
require("@itwin/itwinui-css/css/carousel.css");
const CarouselContext_js_1 = require("./CarouselContext.js");
const CarouselSlider_js_1 = require("./CarouselSlider.js");
const CarouselSlide_js_1 = require("./CarouselSlide.js");
const CarouselDotsList_js_1 = require("./CarouselDotsList.js");
const CarouselDot_js_1 = require("./CarouselDot.js");
const CarouselNavigation_js_1 = require("./CarouselNavigation.js");
/**
 * The Carousel component consists of a set of slides, normally displayed one at a time. A navigation section is
 * located below the slides, consisting of "dots" and "previous"/"next" buttons, used for changing slides.
 *
 * The currently shown slide can also be changed using the left/right arrow keys or by dragging on a touch device.
 *
 * This component uses a composition approach so it should be used with the provided subcomponents.
 *
 * @example
 * <Carousel>
 *   <Carousel.Slider>
 *     <Carousel.Slide>...</Carousel.Slide>
 *     <Carousel.Slide>...</Carousel.Slide>
 *     <Carousel.Slide>...</Carousel.Slide>
 *   </Carousel.Slider>
 *   <Carousel.Navigation />
 * </Carousel>
 */
exports.Carousel = Object.assign(React.forwardRef((props, ref) => {
    const { activeSlideIndex: userActiveIndex = 0, onSlideChange, className, children, ...rest } = props;
    // Generate a stateful random id if not specified
    const [id] = React.useState(() => { var _a; return (_a = props.id) !== null && _a !== void 0 ? _a : `iui-carousel-${(0, index_js_1.getRandomValue)(10)}`; });
    (0, index_js_1.useTheme)();
    const isManuallyUpdating = React.useRef(false);
    const carouselRef = React.useRef(null);
    const refs = (0, index_js_1.useMergedRefs)(carouselRef, ref);
    const [currentIndex, setCurrentIndex] = React.useState(userActiveIndex);
    const scrollToSlide = React.useRef(() => { }); // stub function populated in CarouselSlider
    const justMounted = React.useRef(true);
    React.useEffect(() => {
        var _a;
        setCurrentIndex(userActiveIndex);
        scrollToSlide.current(userActiveIndex, {
            instant: justMounted.current,
        });
        // re-focus the carousel for keyboard nav, but not on first mount
        // because it shows outline and might interfere with other components
        if (!justMounted.current) {
            (_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        }
        justMounted.current = false;
    }, [userActiveIndex]);
    const [slideCount, setSlideCount] = React.useState(0);
    const [keysPressed, setKeysPressed] = React.useState({});
    const handleKeyDown = (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        const key = event.key;
        if (key === 'ArrowLeft' || key === 'ArrowRight') {
            setKeysPressed((old) => ({ ...old, [key]: true }));
        }
    };
    const handleKeyUp = (event) => {
        switch (event.key) {
            case 'ArrowLeft': {
                setKeysPressed((old) => ({ ...old, ArrowLeft: false }));
                const prevIndex = (slideCount + currentIndex - 1) % slideCount;
                scrollToSlide.current(prevIndex);
                setCurrentIndex(prevIndex);
                break;
            }
            case 'ArrowRight': {
                setKeysPressed((old) => ({ ...old, ArrowRight: false }));
                const nextIndex = (slideCount + currentIndex + 1) % slideCount;
                scrollToSlide.current(nextIndex);
                setCurrentIndex(nextIndex);
                break;
            }
        }
    };
    const userOnSlideChange = React.useRef(onSlideChange);
    React.useEffect(() => {
        var _a;
        (_a = userOnSlideChange.current) === null || _a === void 0 ? void 0 : _a.call(userOnSlideChange, currentIndex);
    }, [currentIndex]);
    return (React.createElement("section", { "aria-roledescription": 'carousel', tabIndex: 0, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, ref: refs, className: (0, classnames_1.default)('iui-carousel', className), ...rest, id: id },
        React.createElement(CarouselContext_js_1.CarouselContext.Provider, { value: {
                currentIndex,
                setCurrentIndex,
                slideCount,
                setSlideCount,
                keysPressed,
                idPrefix: id,
                isManuallyUpdating,
                scrollToSlide,
            } }, children)));
}), {
    Slider: CarouselSlider_js_1.CarouselSlider,
    Slide: CarouselSlide_js_1.CarouselSlide,
    Navigation: CarouselNavigation_js_1.CarouselNavigation,
    DotsList: CarouselDotsList_js_1.CarouselDotsList,
    Dot: CarouselDot_js_1.CarouselDot,
});
exports.default = exports.Carousel;
